import { DatePicker, Form, Input } from 'antd';
import { useContext } from 'react';

import { DataContext } from '../context';
import { kennitalaValidator, kennitalaValidatorOnChange, kennitalaNormalize } from '../utils';
import { useFormatMessage } from '../hooks'

const { Item } = Form

const maxLength = 100 // we need some reasonable limit

const fieldConfigs = {
  createDate: {
    default: true,
  },
  createdByUserName: {
    default: true,
  },
  businessKey: {
    default: true,
  },
  kennitala: {
    normalize: kennitalaNormalize, // TODO: normalize is garbage as cursor position gets reset
    rules: [
      {
        validateTrigger: ['onChange'],
        validator: kennitalaValidatorOnChange,
      },
      {
        validateTrigger: ['onBlur', 'onSubmit'],
        validator: kennitalaValidator,
      },
    ]
  },
  recipientName: {
  },
  street: {
  },
  senderOrderId: {
  },
  description: {
  },
  currentState: {
    default: true,
  },
  location: {
    default: true,
  },
  // TODO should be called lockerStatus
  apexStatus: {
    default: true,
  },
  branchName: {
    default: true,
  },
  paymentAmount: {
    normalize: (value) => {
      if (value === undefined || value === null || value === '') return value;
      const digitsOnly = String(value).replace(/[^0-9]/g, '');
      return digitsOnly;
    },
    rules: [
      {
        validateTrigger: ['onBlur', 'onSubmit', 'onChange'],
        validator: (_rule, value, f) => {
          if (value === undefined || value === null || value === '') return Promise.resolve();
          const str = String(value).trim();
          if (!/^\d+$/.test(str)) {
            return Promise.reject(new Error(f('Validation.NonNegativeInteger')));
          }
          const n = Number.parseInt(str, 10);
          if (Number.isNaN(n) || n < 0) {
            return Promise.reject(new Error(f('Validation.NonNegativeInteger')));
          }
          return Promise.resolve();
        },
      },
    ],
  },
  amountDue: {
    normalize: (value) => {
      if (value === undefined || value === null || value === '') return value;
      const digitsOnly = String(value).replace(/[^0-9]/g, '');
      return digitsOnly;
    },
  },
  paidAmount: {
    normalize: (value) => {
      if (value === undefined || value === null || value === '') return value;
      const digitsOnly = String(value).replace(/[^0-9]/g, '');
      return digitsOnly;
    },
  },
  licensePlate: {
    normalize: (value) => {
      if (value === undefined || value === null) return value;
      const str = String(value);
      return str.toLocaleUpperCase();
    },
  }
}

/**
 * Reusable Input Field, with validation, that could be readOnly or disabled.
 * @prop {name} string (required)
 * @prop {disabled} bool (optional)
 * @prop {readOnly} bool (optional)
 */
const InputField = ({ name, disabled = false, readOnly = false, labelSelector = null }) => {
  const f = useFormatMessage()
  const { fields } = useContext(DataContext)

  const fieldUsed = name => fields.get(name)?.used ?? false
  const isRequired = name => fields.get(name)?.required ?? false

  const fieldConfig = fieldConfigs[name] ?? {}

  if (!(fieldConfig.default || fieldUsed(name))) return null

  const baseRule = {
    type: "string",
    whitespace: true,
    required: isRequired(name)
  }

  const extraRules = (fieldConfig.rules ?? []).map(rule => ({
    ...rule,
    // we get the message from the validator and need to pass in the formatter
    validator: (ruleParam, value) => rule.validator(ruleParam, value, f)
  }))

  const rules = [baseRule].concat(extraRules)
  let labelSelectorId = labelSelector ?? `Delivery.${name}.label`
  //HACK: we need to do this better
  if (name === "arrivalTime" || name === "departureTime") {
    const dateRules = [{ required: isRequired(name) }];
    if (readOnly || disabled) {
      return (
        <Item
          name={name}
          label={f(labelSelectorId)}
          valuePropName="value"
          messageVariables={{ msgLabel: f(`Delivery.${name}.validationName`) }}
          rules={rules}
          normalize={fieldConfig.normalize}
          validateTrigger={["onBlur", "onSubmit", "onChange"]}
          hasFeedback
        >
          <Input readOnly={readOnly} disabled={disabled} maxLength={maxLength}></Input>
        </Item>
      )
    }
    return (
      <Item
        name={name}
        label={f(labelSelectorId)}
        valuePropName="value"
        messageVariables={{ msgLabel: f(`Delivery.${name}.validationName`) }}
        rules={dateRules}
        validateTrigger={["onBlur", "onSubmit"]}
        hasFeedback
      >
        <DatePicker showTime={{ format: "HH:mm" }}></DatePicker>
      </Item>
    )
  }

  return (
    <Item
      name={name}
      label={f(labelSelectorId)}
      valuePropName="value"
      messageVariables={{ msgLabel: f(`Delivery.${name}.validationName`) }}
      rules={rules}
      normalize={fieldConfig.normalize}
      validateTrigger={["onBlur", "onSubmit", "onChange"]}
      hasFeedback
    >
      <Input readOnly={readOnly} disabled={disabled} maxLength={maxLength}></Input>
    </Item>
  )
}

export default InputField
